import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Upload, LogOut, CheckCircle, AlertCircle, Loader2, ChevronDown, ToggleLeft, ToggleRight, Umbrella } from 'lucide-react';
import { supabase } from '@/lib/supabase';

const KARTES = [
  { key: 'wochenkarte', label: 'Wochenkarte' },
  { key: 'speisekarte', label: 'Speisekarte' },
  { key: 'mitnahme', label: 'Mitnahmekarte' },
  { key: 'getraenke', label: 'Getränkekarte' },
];

// CSV-Format je Karte (Trennzeichen: Semikolon):
// Wochenkarte:  category;category_title;name;price
// Speisekarte:  category;category_title;category_subtitle;name;description;price
// Mitnahme:     category;category_title;category_subtitle;name;description;price
// Getränke:     category;category_title;category_subtitle;name;size;price

// Stellt sicher, dass ein Preis wie "7,5" immer als "7,50" gespeichert wird
function normalizePrice(val) {
  if (!val) return val;
  return val.replace(/,(\d)(?=\s|$|\/)/g, ',$10');
}

function parseCSV(text) {
  const lines = text.trim().split('\n');
  const headers = lines[0].split(';').map((h) => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map((line, idx) => {
    const values = line.split(';').map((v) => v.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((h, i) => { row[h] = values[i] ?? ''; });
    row.sort_order = idx;
    return row;
  });
}

// --- Login-Formular ---
function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) { setError('Ungültige Zugangsdaten.'); return; }
    onLogin();
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-lg p-10 w-full max-w-sm"
      >
        <h1 className="font-heading text-3xl text-olive-900 mb-2">Admin</h1>
        <p className="font-body text-muted-foreground text-sm mb-8">Bitte anmelden</p>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block font-body text-sm text-olive-900 mb-1">E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-olive-900/20 px-3 py-2 font-body text-sm focus:outline-none focus:border-terracotta"
            />
          </div>
          <div>
            <label className="block font-body text-sm text-olive-900 mb-1">Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full border border-olive-900/20 px-3 py-2 font-body text-sm focus:outline-none focus:border-terracotta"
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm font-body">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-terracotta text-white font-body uppercase text-sm tracking-widest py-3 hover:bg-terracotta/90 transition-colors flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Anmelden
          </button>
        </form>
      </motion.div>
    </div>
  );
}

// --- CSV-Upload-Block je Karte ---
function UploadBlock({ karte }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleFile = useCallback((e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setStatus(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCSV(ev.target.result);
      setPreview(rows);
      setPreviewOpen(true);
    };
    reader.readAsText(f, 'UTF-8');
  }, []);

  const handleUpload = async () => {
    if (!preview.length) return;
    setStatus('loading');
    setErrorMsg('');

    // Alte Einträge dieser Karte löschen
    const { error: delErr } = await supabase
      .from('menu_items')
      .delete()
      .eq('karte', karte.key);

    if (delErr) { setStatus('error'); setErrorMsg(delErr.message); return; }

    // Neue Einträge einspielen
    const rows = preview.map((row) => ({
      karte: karte.key,
      category: row.category ?? '',
      category_title: row.category_title ?? '',
      category_subtitle: row.category_subtitle ?? null,
      sort_order: Number(row.sort_order) || 0,
      name: row.name ?? '',
      description: row.description || null,
      price: normalizePrice(row.price ?? ''),
      size: row.size || null,
    }));

    const { data: insData, error: insErr } = await supabase.from('menu_items').insert(rows).select();
    if (insErr) { setStatus('error'); setErrorMsg(insErr.message); return; }
    if (!insData || insData.length === 0) {
      setStatus('error');
      setErrorMsg('Daten wurden nicht gespeichert. Bitte RLS-Policy in Supabase prüfen (INSERT-Berechtigung für authenticated fehlt).');
      return;
    }

    setStatus('success');
    setFile(null);
    setPreview([]);
    setPreviewOpen(false);
  };

  return (
    <div className="bg-white shadow-sm border border-olive-900/10 p-6">
      <h2 className="font-heading text-xl text-olive-900 mb-4">{karte.label}</h2>

      {/* CSV-Format Hinweis */}
      <details className="mb-4">
        <summary className="font-body text-xs text-muted-foreground cursor-pointer hover:text-olive-900">
          CSV-Format anzeigen
        </summary>
        <div className="mt-2 bg-cream p-3 font-mono text-xs text-olive-900 overflow-x-auto">
          {karte.key === 'wochenkarte' && 'category,category_title,name,price'}
          {karte.key === 'speisekarte' && 'category,category_title,category_subtitle,name,description,price'}
          {karte.key === 'mitnahme' && 'category,category_title,category_subtitle,name,description,price'}
          {karte.key === 'getraenke' && 'category,category_title,category_subtitle,name,size,price'}
        </div>
      </details>

      {/* Datei-Upload */}
      <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-olive-900/30 hover:border-terracotta cursor-pointer transition-colors">
        <Upload size={18} className="text-olive-900/60" />
        <span className="font-body text-sm text-olive-900/70">
          {file ? file.name : 'CSV-Datei auswählen...'}
        </span>
        <input type="file" accept=".csv" className="hidden" onChange={handleFile} />
      </label>

      {/* Vorschau */}
      {preview.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setPreviewOpen(!previewOpen)}
            className="flex items-center gap-2 font-body text-sm text-olive-900 mb-2"
          >
            <ChevronDown
              size={16}
              className={`transition-transform ${previewOpen ? 'rotate-180' : ''}`}
            />
            Vorschau ({preview.length} Einträge)
          </button>
          {previewOpen && (
            <div className="overflow-x-auto max-h-48 overflow-y-auto border border-olive-900/10">
              <table className="w-full text-xs font-body">
                <thead className="bg-cream sticky top-0">
                  <tr>
                    {Object.keys(preview[0]).filter(k => k !== 'sort_order').map((h) => (
                      <th key={h} className="text-left px-2 py-1 text-olive-900 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => (
                    <tr key={i} className="border-t border-olive-900/5">
                      {Object.entries(row).filter(([k]) => k !== 'sort_order').map(([k, v]) => (
                        <td key={k} className="px-2 py-1 text-olive-900/80">{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={status === 'loading'}
            className="mt-3 bg-olive-900 text-cream font-body uppercase text-xs tracking-widest px-5 py-2.5 hover:bg-olive-900/80 transition-colors flex items-center gap-2"
          >
            {status === 'loading' && <Loader2 size={14} className="animate-spin" />}
            Jetzt hochladen & überschreiben
          </button>
        </div>
      )}

      {/* Status */}
      {status === 'success' && (
        <div className="mt-3 flex items-center gap-2 text-green-700 font-body text-sm">
          <CheckCircle size={16} /> Erfolgreich gespeichert!
        </div>
      )}
      {status === 'error' && (
        <div className="mt-3 flex items-center gap-2 text-red-600 font-body text-sm">
          <AlertCircle size={16} /> Fehler: {errorMsg}
        </div>
      )}
    </div>
  );
}

// --- Urlaubsmeldung-Block ---
function VacationNoticeBlock() {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // null | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    supabase
      .from('vacation_notice')
      .select('*')
      .eq('id', 1)
      .single()
      .then(({ data }) => {
        if (data) {
          setTitle(data.title || '');
          setDescription(data.description || '');
          setActive(data.active || false);
        }
        setLoading(false);
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    const { error } = await supabase
      .from('vacation_notice')
      .upsert({ id: 1, title, description: description || null, active, updated_at: new Date().toISOString() });
    setSaving(false);
    if (error) { setStatus('error'); setErrorMsg(error.message); }
    else setStatus('success');
  };

  if (loading) return <div className="font-body text-sm text-olive-900/50">Lade...</div>;

  return (
    <div className="bg-white shadow-sm border border-olive-900/10 p-6 space-y-5">
      <div className="flex items-center gap-3">
        <Umbrella size={20} className="text-terracotta" />
        <h2 className="font-heading text-xl text-olive-900">Urlaubsmeldung</h2>
      </div>

      {/* Titel */}
      <div>
        <label className="block font-body text-sm text-olive-900 mb-1">
          Titel <span className="text-terracotta">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="z. B. Wir haben Urlaub vom 23.04 – 03.05.2026"
          className="w-full border border-olive-900/20 px-3 py-2 font-body text-sm focus:outline-none focus:border-terracotta"
        />
      </div>

      {/* Beschreibung */}
      <div>
        <label className="block font-body text-sm text-olive-900 mb-1">
          Beschreibung <span className="text-olive-900/40 font-normal">(optional)</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="z. B. Wir befinden uns in den Osterferien und freuen uns…"
          className="w-full border border-olive-900/20 px-3 py-2 font-body text-sm focus:outline-none focus:border-terracotta resize-none"
        />
      </div>

      {/* Aktivieren-Toggle */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setActive((v) => !v)}
          className="flex items-center gap-2 font-body text-sm transition-colors"
          aria-pressed={active}
        >
          {active
            ? <ToggleRight size={28} className="text-terracotta" />
            : <ToggleLeft size={28} className="text-olive-900/30" />}
          <span className={active ? 'text-terracotta font-semibold' : 'text-olive-900/60'}>
            {active ? 'Banner aktiv – sichtbar für Besucher' : 'Banner inaktiv – nicht sichtbar'}
          </span>
        </button>
      </div>

      {/* Speichern */}
      <button
        onClick={handleSave}
        disabled={saving || !title.trim()}
        className="bg-olive-900 text-cream font-body uppercase text-xs tracking-widest px-5 py-2.5 hover:bg-olive-900/80 transition-colors flex items-center gap-2 disabled:opacity-40"
      >
        {saving && <Loader2 size={14} className="animate-spin" />}
        Speichern
      </button>

      {status === 'success' && (
        <div className="flex items-center gap-2 text-green-700 font-body text-sm">
          <CheckCircle size={16} /> Gespeichert!
        </div>
      )}
      {status === 'error' && (
        <div className="flex items-center gap-2 text-red-600 font-body text-sm">
          <AlertCircle size={16} /> Fehler: {errorMsg}
        </div>
      )}
    </div>
  );
}

// --- Haupt-Admin-Seite ---
export default function Admin() {
  const [session, setSession] = useState(null);
  const [checked, setChecked] = useState(false);
  const [activeTab, setActiveTab] = useState('karten');

  // Beim Laden prüfen ob noch eingeloggt
  useState(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setChecked(true);
    });
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
  };

  if (!checked) return null;
  if (!session) return <LoginForm onLogin={async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
  }} />;

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-olive-900 py-6 px-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl text-cream">Admin – Il Girasole</h1>
          <p className="font-body text-cream/60 text-sm mt-0.5">{session.user.email}</p>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 font-body text-sm text-cream/80 hover:text-cream transition-colors"
        >
          <LogOut size={16} />
          Abmelden
        </button>
      </div>

      {/* Tab-Navigation */}
      <div className="border-b border-olive-900/10 bg-white">
        <div className="max-w-4xl mx-auto px-4 flex gap-0">
          {['karten', 'urlaub'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`font-body text-sm uppercase tracking-widest px-5 py-4 border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-terracotta text-terracotta'
                  : 'border-transparent text-olive-900/50 hover:text-olive-900'
              }`}
            >
              {tab === 'karten' ? 'Speisekarten' : 'Urlaubsmeldung'}
            </button>
          ))}
        </div>
      </div>

      {/* Inhalt */}
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        {activeTab === 'karten' && (
          <>
            <div>
              <h2 className="font-heading text-3xl text-olive-900 mb-2">Karten verwalten</h2>
              <p className="font-body text-muted-foreground text-sm leading-relaxed">
                Lade für jede Karte eine CSV-Datei hoch. Der Inhalt wird vollständig ersetzt.
                <br />
                Die Änderungen sind sofort für alle Besucher sichtbar.
              </p>
            </div>
            {KARTES.map((karte) => (
              <UploadBlock key={karte.key} karte={karte} />
            ))}
          </>
        )}

        {activeTab === 'urlaub' && (
          <>
            <div>
              <h2 className="font-heading text-3xl text-olive-900 mb-2">Urlaubsmeldung</h2>
              <p className="font-body text-muted-foreground text-sm leading-relaxed">
                Hinterlege einen Hinweis für Besucher. Das Banner erscheint als Overlay auf der gesamten Website,
                solange es aktiv ist.
              </p>
            </div>
            <VacationNoticeBlock />
          </>
        )}
      </div>
    </div>
  );
}
